import {Store} from 'Application/Store/Store'
import {AnimationMixer, Group, Box3, QuaternionKeyframeTrack, VectorKeyframeTrack} from 'three'
import {
    lineMaterial,
    gengivaMaterial,
    attachmentPosMaterial,
    attachmentNegMaterial,
    strippingMaterial,
    ponticMaterial,
} from 'Domain/Scene/Provider/MaterialProvider'
import materialPool from 'Domain/Scene/Provider/MaterialPool'
import {MainCameraPosition} from 'Domain/Scene/Handler/CameraHandler'
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine'
import {
    addGengiva,
    getTimes,
    assignTimelineStep,
    archMatrix,
    addPontic,
    fillGengivaComparative
} from 'Domain/Scene/Handler/AnimationHandler'
import {setMeshToLayer} from 'Domain/Scene/Handler/LayerHandler'
import {getJaw} from 'Domain/Mesh/Provider/MeshProvider'
import {addMarkups} from 'Domain/Scene/Handler/StrippingHandler'
import {getErrorObject} from 'Domain/Scene/Handler/ErrorHandler'
import { getExtraData } from 'Domain/Mesh/Factory/ToothFactory';
import { dispatchPlayerIncrementation, dispatchDeniedServices } from 'Domain/Mesh/State/ToothStateDispatchers';
import {addAttachments} from "../Handler/AttachmentHandler";
import {addMeshInComparativeGroup} from "../Handler/ComparativeViewHandler";
import { SET_VIEWER_TYPE } from 'Application/Store/Actions/ViewportTypes'
import { nextRenderTick } from 'Domain/Scene/Manager/InitializationOrchestrator'

const ComparativeSceneGroup = Object.assign(new Group(), { name: "ComparativeScene" });
threeEngine.setComparativeSceneGroup(ComparativeSceneGroup);

/**
 * Point d'entrée principal : charge et hydrate la scène avec tous les éléments
 * @param {blob} in_model - Le fichier GLTF
 * @param {Vector3} in_center - Le centre de la scène
 * @param {Scene} in_scene - La scène Three.js
 * @param {WebGLRenderer} renderer - Le renderer (non utilisé actuellement)
 * @returns {object} { maxiExtraData, mandiExtraData }
 */
export async function addSceneElements(in_model, in_center, in_scene) {
    try {
        threeEngine.setAllAttachments([]);
        const tempGroup = threeEngine.getTempGroup();
        if (tempGroup) {
            in_scene.add(tempGroup);
        }
        
        const { gltf, markupGroup, maxiMarkupGroup, mandiMarkupGroup } = 
            await loadGltfModel(in_model, in_center);
        
        addGltfMeshesToScene(gltf, in_scene);
        
        await setupStrippingMarkups(gltf.userData, in_center, maxiMarkupGroup, mandiMarkupGroup);
        
        addElementsToScene(in_scene, gltf, markupGroup);
        
        await setupAnimations(gltf);
        
        dispatchUserData(gltf.userData);
        
        extractMarksAndStrippings(gltf, in_scene);
        
        await setupComparativeView(in_scene, gltf);
        
        return await getExtraData(gltf, in_scene, false);
        
    } catch (error) {
        console.error("Erreur lors de l'ajout des éléments à la scène :", error);
        throw error;
    }
}

/**
 * Charge le modèle GLTF et initialise les structures de base
 * @param {blob} in_model - Le fichier GLTF à charger
 * @param {Vector3} in_center - Le centre de la scène
 * @returns {object} { gltf, markupGroup, maxiMarkupGroup, mandiMarkupGroup }
 */
async function loadGltfModel(in_model, in_center) {
    const gltf = await loadGltfFile(in_model);
    calculateSceneCenter(gltf, in_center);
    const { markupGroup, maxiMarkupGroup, mandiMarkupGroup } = createMarkupGroups();
    gltf.scene.name = 'importedMeshes';
    return { gltf, markupGroup, maxiMarkupGroup, mandiMarkupGroup };
}

/**
 * Charge le fichier GLTF
 * @param {blob} in_model - Le fichier GLTF
 * @returns {object} Le modèle GLTF chargé
 */
async function loadGltfFile(in_model) {
    return await getJaw(in_model);
}

/**
 * Calcule le centre de la scène
 * @param {object} in_gltf - Le modèle GLTF
 * @param {Vector3} in_center - Le centre à modifier
 */
function calculateSceneCenter(in_gltf, in_center) {
    const bbox = new Box3().setFromObject(in_gltf.scene);
    in_center.set(0, 0, bbox.getCenter(in_center).z);
}

/**
 * Crée les groupes de markup
 * @returns {object} { markupGroup, maxiMarkupGroup, mandiMarkupGroup }
 */
function createMarkupGroups() {
    const markupGroup = new Group();
    const maxiMarkupGroup = new Group();
    threeEngine.setMandiMarkupGroup(new Group());
    
    maxiMarkupGroup.name = 'maxiMarkups';
    threeEngine.getMandiMarkupGroup().name = 'mandiMarkups';
    markupGroup.name = 'markupGroup';
    markupGroup.add(maxiMarkupGroup, threeEngine.getMandiMarkupGroup());
    
    return { markupGroup, maxiMarkupGroup, mandiMarkupGroup: threeEngine.getMandiMarkupGroup() };
}

/**
 * Ajoute les meshes du GLTF à la scène avec leurs matériaux et animations
 * @param {object} in_gltf - Le modèle GLTF chargé
 * @param {Scene} in_scene - La scène Three.js
 */
function addGltfMeshesToScene(in_gltf, in_scene) {
    const mapToothAnimation = createToothAnimationMapper(in_gltf);
    
    in_gltf.scene.children.forEach(mesh => {
        if (in_gltf.userData.viewerType === "simple") {
            Store.dispatch({ type: SET_VIEWER_TYPE, payload: 'simple' });
            return;
        }
 
        const arch = archMatrix[mesh.userData.jawType];
        processMesh(mesh, arch, mapToothAnimation, in_scene);
    });
}

/**
 * Crée une fonction pour mapper les animations des dents
 * @param {object} in_gltf - Le modèle GLTF
 * @returns {function} Fonction de mapping des animations
 */
function createToothAnimationMapper(in_gltf) {
    const getAnimationSpec = (toothId, spec) => 
        in_gltf.animations[0].tracks.filter(track => 
            track.name === '0_Tooth_' + toothId + '.' + spec
        )[0];
    
    return mesh => {
        const name = mesh.name;
        const position = getAnimationSpec(mesh.userData.toothID, 'position');
        const quaternion = getAnimationSpec(mesh.userData.toothID, 'quaternion');
        
        in_gltf.animations[0].tracks.push(
            new QuaternionKeyframeTrack(name + '.quaternion', quaternion.times, quaternion.values),
            new VectorKeyframeTrack(name + '.position', position.times, position.values)
        );
    };
}

/**
 * Traite un mesh individuel
 * @param {Mesh} in_mesh - Le mesh à traiter
 * @param {string} in_arch - L'arcade
 * @param {function} in_mapToothAnimation - Fonction de mapping des animations
 * @param {Scene} in_scene - La scène Three.js
 */
function processMesh(in_mesh, in_arch, in_mapToothAnimation, in_scene) {
    computeMeshNormals(in_mesh);
    setMeshToLayer(in_mesh, in_arch);
    addToMandibularCollectionIfNeeded(in_mesh);
    setupMeshMaterial(in_mesh, in_arch, in_mapToothAnimation, in_scene);
    addToComparativeGroupIfNeeded(in_mesh, in_arch);
}

/**
 * Calcule les normales d'un mesh
 * @param {Mesh} in_mesh - Le mesh
 */
function computeMeshNormals(in_mesh) {
    in_mesh.geometry.computeVertexNormals();
}

/**
 * Ajoute le mesh aux collections mandibulaires si nécessaire
 * @param {Mesh} in_mesh - Le mesh
 */
function addToMandibularCollectionIfNeeded(in_mesh) {
    if (in_mesh.userData.jawType === 'L') {
        if (in_mesh.userData.type !== 2 && in_mesh.userData.type !== 0 && in_mesh.userData.type !== 3) {
            threeEngine.addToAllMandiStuff(in_mesh);
        } else if (in_mesh.userData.type === 0 || in_mesh.userData.type === 3) {
            threeEngine.getAllMandiTeeth().push(in_mesh);
        }
    }
}

/**
 * Ajoute le mesh au groupe comparatif si nécessaire
 * @param {Mesh} in_mesh - Le mesh
 * @param {string} in_arch - L'arcade
 */
function addToComparativeGroupIfNeeded(in_mesh, in_arch) {
    if (in_mesh.userData.type === 0 || in_mesh.userData.type === 2 || in_mesh.userData.type === 4) {
        if (in_mesh.userData.type === 4 && parseInt(in_mesh.userData.step) !== 0) return;
        addMeshInComparativeGroup(in_mesh, in_arch);
    }
}

/**
 * Applique le bon matériau à un mesh selon son type
 * @param {Mesh} in_mesh - Le mesh à configurer
 * @param {string} in_arch - L'arcade ('upper' ou 'lower')
 * @param {function} in_mapToothAnimation - Fonction pour mapper les animations
 * @param {Scene} in_scene - La scène Three.js
 */
function setupMeshMaterial(in_mesh, in_arch, in_mapToothAnimation, in_scene) {
    switch (in_mesh.userData.type) {
        case 0:
            setupToothMaterial(in_mesh);
            break;
            
        case 1:
            setupGengivaMaterial(in_mesh, in_arch);
            break;
            
        case 2:
            setupAttachmentMaterial(in_mesh);
            break;
            
        case 3:
            setupStrippingMaterial(in_mesh, in_mapToothAnimation);
            break;
            
        case 4:
            setupPonticMaterial(in_mesh);
            break;
            
        case 9:
            setupErrorMaterial(in_mesh, in_scene);
            break;
            
        default:
            setupDefaultMaterial(in_mesh);
    }
}

/**
 * Configure le matériau pour une dent
 * @param {Mesh} in_mesh - Le mesh de la dent
 */
function setupToothMaterial(in_mesh) {
    in_mesh.material = materialPool.acquire('tooth');
}

/**
 * Configure le matériau pour une gencive
 * @param {Mesh} in_mesh - Le mesh de la gencive
 * @param {string} in_arch - L'arcade
 */
function setupGengivaMaterial(in_mesh, in_arch) {
    addGengiva(in_mesh, in_arch, in_mesh.userData.step);
    in_mesh.visible = false;
    in_mesh.material = gengivaMaterial(in_mesh.geometry);
}

/**
 * Configure le matériau pour un attachement
 * @param {Mesh} in_mesh - Le mesh de l'attachement
 */
function setupAttachmentMaterial(in_mesh) {
    in_mesh.material = (in_mesh.userData.attachmentType === 'P') 
        ? attachmentPosMaterial 
        : attachmentNegMaterial;
}

/**
 * Configure le matériau pour un stripping
 * @param {Mesh} in_mesh - Le mesh du stripping
 * @param {function} in_mapToothAnimation - Fonction pour mapper les animations
 */
function setupStrippingMaterial(in_mesh, in_mapToothAnimation) {
    in_mesh.material = strippingMaterial;
    in_mesh.renderOrder = 1;
    in_mapToothAnimation(in_mesh);
}

/**
 * Configure le matériau pour un pontic
 * @param {Mesh} in_mesh - Le mesh du pontic
 */
function setupPonticMaterial(in_mesh) {
    if (in_mesh.userData.step === undefined) {
        in_mesh.userData.step = 0;
    }
    addPontic(in_mesh, in_mesh.name.split('_')[2], in_mesh.userData.step);
    in_mesh.visible = false;
    in_mesh.material = ponticMaterial;
}

/**
 * Configure le matériau pour une erreur
 * @param {Mesh} in_mesh - Le mesh d'erreur
 * @param {Scene} in_scene - La scène Three.js
 */
function setupErrorMaterial(in_mesh, in_scene) {
    in_mesh.material = threeEngine.getConeMaterial();
    in_mesh.position.set(0, 0, 0);
    const errorObj = getErrorObject(404);
    in_scene.add(errorObj);
    MainCameraPosition(0, -12, 4);
}

/**
 * Configure le matériau par défaut
 * @param {Mesh} in_mesh - Le mesh
 */
function setupDefaultMaterial(in_mesh) {
    in_mesh.material = materialPool.acquire('tooth');
    MainCameraPosition(0, -200, 100);
}

/**
 * Configure et ajoute les markups de stripping à la scène
 * @param {object} userData - Les données utilisateur du GLTF
 * @param {Vector3} in_center - Le centre de la scène
 * @param {Group} maxiMarkupGroup - Groupe pour les markups maxillaires
 * @param {Group} mandiMarkupGroup - Groupe pour les markups mandibulaires
 */
async function setupStrippingMarkups(userData, in_center, maxiMarkupGroup, mandiMarkupGroup) {
    if (!userData.ipr) return;
    
    Object.keys(archMatrix).forEach(arch => {
        const targetGroup = arch === 'U' ? maxiMarkupGroup : mandiMarkupGroup;
        addMarkups(userData.ipr, in_center, arch, lineMaterial, targetGroup);
    });
}

/**
 * Ajoute les éléments GLTF à la scène
 * @param {Scene} scene - La scène Three.js
 * @param {object} gltf - Le modèle GLTF
 * @param {Group} markupGroup - Le groupe de markups
 */
function addElementsToScene(scene, gltf, markupGroup) {
    if (gltf.userData.ipr) {
        scene.add(markupGroup, gltf.scene);
    } else {
        scene.add(gltf.scene);
    }
}

/**
 * Configure le mixer et les animations du modèle
 * @param {object} gltf - Le modèle GLTF
 */
async function setupAnimations(gltf) {
    createAnimationMixer(gltf);
    
    if (gltf.animations[0] !== undefined) {
        await configureAnimation(gltf.animations[0]);
        dispatchAnimationToStore();
    }
}

/**
 * Crée le mixer d'animation
 * @param {object} gltf - Le modèle GLTF
 */
function createAnimationMixer(gltf) {
    threeEngine.setMixer(new AnimationMixer(gltf.scene));
}

/**
 * Configure l'action d'animation
 * @param {AnimationClip} animationClip - Le clip d'animation
 */
async function configureAnimation(animationClip) {
    threeEngine.setAction(threeEngine.getMixer().clipAction(animationClip));
    threeEngine.getAction().timeScale = 0;
    threeEngine.getAction().play();
    await assignTimelineStep(animationClip);
}

/**
 * Dispatch l'animation au store
 */
function dispatchAnimationToStore() {
    Store.dispatch({ type: 'INIT_ANIMATION', payload: getTimes() });
}

/**
 * Dispatch les données utilisateur au store
 * @param {object} userData - Les données utilisateur du GLTF
 */
function dispatchUserData(userData) {
    dispatchPlayerIncrementation(userData);
    dispatchDeniedServices(userData);
    Store.dispatch({ type: 'INIT_METAS', payload: userData });
}

/**
 * Extrait les références de marks et strippings si userData existe
 * @param {object} gltf - Le modèle GLTF
 * @param {Scene} scene - La scène Three.js
 */
function extractMarksAndStrippings(gltf, scene) {
    if (Object.keys(gltf.userData).length !== 0) {
        threeEngine.setMarks(scene.children.filter(child => child.name === "markupGroup")[0]);
        const meshes = scene.children.filter(child => child.name === 'importedMeshes')[0];
        threeEngine.setStrippings(meshes.children.filter(child => child.userData.type === 3));
    }
}

/**
 * Configure tous les éléments clonés et artificiels pour la vue comparative
 * @param {Scene} scene - La scène Three.js
 * @param {object} gltf - Le modèle GLTF
 * @returns {object} { maxiExtraData, mandiExtraData }
 */
async function setupComparativeView(scene, gltf) {
    addComparativeGengiva(scene);
    addComparativeSceneGroup(scene);
    addMandibularGroup(scene);
    addAllAttachments(scene);
    
    await nextRenderTick();
    
    return await loadAndStoreComparativeExtraData(gltf, scene);
}

/**
 * Ajoute la gencive comparative à la scène
 * @param {Scene} scene - La scène Three.js
 */
function addComparativeGengiva(scene) {
    fillGengivaComparative(scene);
}

/**
 * Ajoute le groupe de scène comparative
 * @param {Scene} scene - La scène Three.js
 */
function addComparativeSceneGroup(scene) {
    scene.add(ComparativeSceneGroup);
}

/**
 * Ajoute le groupe mandibulaire à la scène
 * @param {Scene} scene - La scène Three.js
 */
function addMandibularGroup(scene) {
    const mandiGroup = new Group();
    mandiGroup.name = "mandiGroup";
    scene.add(mandiGroup);
}

/**
 * Ajoute tous les attachements (réels et comparatifs)
 * @param {Scene} scene - La scène Three.js
 */
function addAllAttachments(scene) {
    addAttachments(scene, false);
    addAttachments(scene, true);
}

/**
 * Charge et stocke les données extra pour la vue comparative
 * @param {object} gltf - Le modèle GLTF
 * @param {Scene} scene - La scène Three.js
 * @returns {object} { maxiExtraData, mandiExtraData }
 */
async function loadAndStoreComparativeExtraData(gltf, scene) {
    const { maxiExtraData, mandiExtraData } = await getExtraData(gltf, scene, true);
    threeEngine.setMaxiExtraDataComparative(maxiExtraData);
    threeEngine.setMandiExtraDataComparative(mandiExtraData);
    return { maxiExtraData, mandiExtraData };
}
